import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check } from 'lucide-react';
import { useActiveClinic, type ClinicInfo } from '@/lib/ActiveClinicContext';
import { ClinicLogoBadge } from '@/components/ClinicLogoBadge';
import { useClinicLogos } from '@/hooks/useClinicLogos';
import { cn } from '@/utils/cn';
import { TYPE } from '@/lib/typography';

export function ClinicSelector({
  alwaysShow = false,
  collapsed = false,
}: {
  alwaysShow?: boolean;
  collapsed?: boolean;
}) {
  const { t } = useTranslation();
  const { activeClinic, availableClinics, setActiveClinicId, hasMultipleClinics } = useActiveClinic();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const clinicIds = availableClinics.map((clinic) => clinic.id);
  const { data: logos } = useClinicLogos(clinicIds);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (!alwaysShow && !hasMultipleClinics) return null;
  if (!activeClinic && availableClinics.length === 0) return null;

  const groupedClinics = availableClinics.reduce<{
    main: ClinicInfo[];
    byParent: Record<string, ClinicInfo[]>;
  }>(
    (acc, clinic) => {
      if (!clinic.isSubClinic) {
        acc.main.push(clinic);
      } else if (clinic.parentClinicId) {
        if (!acc.byParent[clinic.parentClinicId]) {
          acc.byParent[clinic.parentClinicId] = [];
        }
        acc.byParent[clinic.parentClinicId].push(clinic);
      }
      return acc;
    },
    { main: [], byParent: {} },
  );

  const handleSelect = (clinicId: string) => {
    setActiveClinicId(clinicId);
    setIsOpen(false);
  };

  const kindLabel = activeClinic?.isSubClinic ? t('nav.sub_clinic') : t('nav.main_clinic');
  const interactive = hasMultipleClinics;
  const clinicName = activeClinic?.name ?? t('nav.active_clinic');

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => interactive && setIsOpen(!isOpen)}
        disabled={!interactive}
        className={cn(
          collapsed
            ? 'flex h-10 w-10 items-center justify-center rounded-ctl'
            : 'flex w-full items-center gap-2.5 rounded-pop border border-ds-line bg-ds-card p-s3 text-left shadow-[0_1px_2px_rgba(0,0,0,.04)]',
          interactive && (collapsed ? 'hover:bg-ds-neutral-tint' : 'hover:bg-ds-sunk'),
        )}
        aria-expanded={interactive ? isOpen : undefined}
        aria-haspopup={interactive ? 'listbox' : undefined}
        aria-label={interactive ? t('nav.switch_clinic') : clinicName}
        title={collapsed ? clinicName : undefined}
      >
        <ClinicLogoBadge
          name={clinicName}
          logoSrc={activeClinic ? (logos?.[activeClinic.id] ?? null) : null}
          initialsTone="current"
        />
        {!collapsed ? (
          <>
            <span className="min-w-0 flex-1">
              <span className={cn(TYPE.control, 'block truncate font-semibold text-ds-text')}>
                {clinicName}
              </span>
              <span className={cn(TYPE.caption, 'block truncate text-ds-muted')}>{kindLabel}</span>
            </span>
            {interactive ? (
              <ChevronDown
                className={cn('h-4 w-4 shrink-0 text-ds-muted transition-transform', isOpen && 'rotate-180')}
              />
            ) : null}
          </>
        ) : null}
      </button>

      {isOpen && interactive && (
        <div
          className={cn(
            'absolute z-50 mt-s2 max-h-80 w-72 overflow-y-auto rounded-pop border border-ds-line bg-ds-card py-s1 shadow-pop',
            collapsed ? 'left-full top-0 ml-s2' : 'left-0',
          )}
          role="listbox"
        >
          {groupedClinics.main.map((clinic) => (
            <div key={clinic.id}>
              <ClinicOption
                clinic={clinic}
                isActive={clinic.id === activeClinic?.id}
                logoSrc={logos?.[clinic.id] ?? null}
                onSelect={handleSelect}
              />
              {groupedClinics.byParent[clinic.id]?.map((subClinic) => (
                <ClinicOption
                  key={subClinic.id}
                  clinic={subClinic}
                  isActive={subClinic.id === activeClinic?.id}
                  logoSrc={logos?.[subClinic.id] ?? null}
                  onSelect={handleSelect}
                  isSubClinic
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClinicOption({
  clinic,
  isActive,
  logoSrc,
  onSelect,
  isSubClinic,
}: {
  clinic: ClinicInfo;
  isActive: boolean;
  logoSrc: string | null;
  onSelect: (id: string) => void;
  isSubClinic?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => onSelect(clinic.id)}
      className={cn(
        'flex w-full items-center gap-s3 px-s3 py-s2 text-left hover:bg-ds-teal-tint',
        isActive && 'bg-ds-teal-tint',
        isSubClinic && 'pl-8',
      )}
      role="option"
      aria-selected={isActive}
    >
      <ClinicLogoBadge
        name={clinic.name}
        logoSrc={logoSrc}
        initialsTone={isActive ? 'active' : 'inactive'}
      />
      <span className="min-w-0 flex-1">
        <span className={cn(TYPE.control, 'block truncate font-medium', isActive ? 'text-ds-teal' : 'text-ds-text')}>
          {clinic.name}
        </span>
        <span className={cn(TYPE.caption, 'text-ds-muted')}>
          {isSubClinic ? t('nav.sub_clinic') : t('nav.main_clinic')}
          {clinic.isActive === false ? ` · ${t('clinics.inactive', 'Inaktiv')}` : ''}
        </span>
      </span>
      {isActive ? <Check className="h-4 w-4 shrink-0 text-ds-teal" /> : null}
    </button>
  );
}
